import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

/**
 * Solomon Button.
 *   - `variant`     → TYPE. Fully owns foreground + surface + line across
 *                     states: raised / inverse-raised / sunken / transparent /
 *                     panel / tab. Each type encodes its own state curve per
 *                     the Figma spec.
 *   - `interaction` → ANIMATION style only. Does not affect colors. One of:
 *                     lifted / light-bar / underline / fillFromTop /
 *                     fillFromBottom.
 *                     `lifted` renders a 3-layer 3D button: thickness wrapper
 *                     + right shadow + inner face. On hover the face lifts
 *                     up (revealing the bottom thickness strip) and the
 *                     shadow slides right (revealing the side strip).
 *                     Strip colors are tokens (`button-lifted-bottom-base`,
 *                     `-bottom-inverse`, `-side`); `bottom-base` is for
 *                     dark-face variants (raised), `-bottom-inverse` is
 *                     for light-face (inverse-raised, sunken, panel).
 *                     Transparent / tab don't get a thickness layer (no
 *                     surface to lift away from).
 *   - `size`        → padding + Text variant: xs/fit/sm → `small`,
 *                     md/lg → `body`. `fit` is sm with zero padding —
 *                     for inline contexts where the Button needs to hug
 *                     surrounding chrome. Typography (family/weight/size/
 *                     tracking/responsive) is owned by `<Text>` variants
 *                     and embedded into the cva via `textVariants()` — no
 *                     raw `text-*`/`font-*` here. `buttonClasses()` callers
 *                     inherit the same.
 *   - `outline`     → 1px border on/off (emitted only for types with a line
 *                     token — raised / inverse-raised / sunken)
 *   - `fullWidth`   → stretch horizontally
 *   - `holdToConfirm` → destructive / intrusive action gate. Pressing and
 *                     holding drives the light-bar progress on the canonical
 *                     button surface, then fires `onHoldConfirm` once.
 *
 * If `interaction` is omitted it defaults to `lifted`. Type + interaction are
 * orthogonal — any combo is valid; dev picks a sensible one for the context.
 *
 * For link-as-button, use `buttonClasses()` helper on a plain `<a>`:
 *   <a href="/x" className={buttonClasses({ variant: "raised", size: "sm" })} />
 */

type ButtonVariant =
    | "raised"
    | "inverse-raised"
    | "sunken"
    | "transparent"
    | "panel"
    | "tab"
    | "toggle"
    | "accordion";
type ButtonInteraction =
    | "default"
    | "lifted"
    | "light-bar"
    | "underline"
    | "fillFromTop"
    | "fillFromBottom";
type ButtonStatus = "idle" | "pending" | "success" | "error";

// Size → Text variant mapping. Typography is owned by `<Text>` variants
// (family + weight + size + tracking + responsive pair); Button just routes
// per-size design tokens through that system. `buttonClasses()` callers
// (link-as-button, trigger slots) inherit the same typography because the
// cva embeds the variant's class chain directly.
const SIZE_TO_TEXT_VARIANT = {
    "2xs": "fine-print",
    xs: "small",
    fit: "small",
    sm: "small",
    md: "body",
    lg: "body"
} as const;

// Size → default inner-content gap. Smaller buttons (small text, fit/xs/sm)
// pair with a tight gap-1 between leading icon + label so they read as one
// inline unit; larger buttons (body text, md/lg) get gap-2 so the icon has
// breathing room against larger glyphs. Override per-call via the `gap`
// prop when the visual rhythm of a specific surface needs something else.
const SIZE_TO_GAP_CLASS = {
    "2xs": "gap-1",
    xs: "gap-1",
    fit: "gap-1",
    sm: "gap-1",
    md: "gap-2",
    lg: "gap-2"
} as const;

// Explicit gap override. Mapped to literal class strings so Tailwind picks
// them up at build time (dynamic `gap-${n}` template strings would not).
type ButtonGap = "0" | "0.5" | "1" | "1.5" | "2" | "2.5" | "3";
const GAP_CLASS: Record<ButtonGap, string> = {
    "0": "gap-0",
    "0.5": "gap-0.5",
    "1": "gap-1",
    "1.5": "gap-1.5",
    "2": "gap-2",
    "2.5": "gap-2.5",
    "3": "gap-3"
};

const buttonVariants = cva(
    "inline-flex flex-row items-center gap-1 transition-colors decoration-[1px] disabled:cursor-not-allowed disabled:pointer-events-none",
    {
        variants: {
            variant: {
                // fg/bg/line constant across non-disabled states per Figma.
                // `group-disabled:` siblings echo `disabled:` so the
                // `lifted` interaction's inner-content span (which is
                // inside the `group` outer button but doesn't itself
                // carry the disabled attribute) still picks up the
                // disabled foreground.
                raised: "text-button-raised-foreground-default bg-button-raised-surface-default disabled:text-button-raised-foreground-disabled group-disabled:text-button-raised-foreground-disabled",
                "inverse-raised":
                    "text-button-inverse-raised-foreground-default bg-button-inverse-raised-surface-default disabled:text-button-inverse-raised-foreground-disabled group-disabled:text-button-inverse-raised-foreground-disabled",
                sunken: "text-button-sunken-foreground-default bg-button-sunken-surface-default disabled:text-button-sunken-foreground-disabled group-disabled:text-button-sunken-foreground-disabled",
                // No surface. Hover = active = aria-selected (all → ember).
                transparent:
                    "text-button-transparent-foreground-default hover:text-button-transparent-foreground-hover active:text-button-transparent-foreground-hover aria-selected:text-button-transparent-foreground-hover disabled:text-button-transparent-foreground-disabled group-disabled:text-button-transparent-foreground-disabled",
                // Persistent surface; aria-selected diverges.
                panel: "text-button-panel-foreground-default bg-button-panel-surface-default aria-selected:bg-button-panel-surface-aria-selected disabled:text-button-panel-foreground-disabled group-disabled:text-button-panel-foreground-disabled",
                // Transparent at idle. Hover is owned by fillFromTop/Bottom;
                // selected + keyboard-focus get persistent focus bg here.
                tab: "text-button-tab-foreground-default aria-selected:bg-button-tab-surface-focus focus-visible:bg-button-tab-surface-focus disabled:text-button-tab-foreground-disabled group-disabled:text-button-tab-foreground-disabled",
                // Toggle — outlined button for ToggleMenu items. Has
                // its own border (no `outline` prop required); five
                // states with full fg + line color curves; aria-selected
                // adds a charcoal surface fill. `transition-[color,
                // background-color, border-color]` with a longer
                // 200ms ease-out-quad duration overrides the base
                // `transition-colors` so the state changes feel
                // deliberate. (Figma node 2058:9078.)
                toggle:
                    "border border-solid bg-transparent justify-center " +
                    "text-button-toggle-foreground-default border-button-toggle-line-default " +
                    "hover:text-button-toggle-foreground-hover hover:border-button-toggle-line-hover " +
                    "active:text-button-toggle-foreground-active active:bg-button-toggle-surface-active active:border-button-toggle-line-active " +
                    "aria-selected:text-button-toggle-foreground-aria-selected aria-selected:bg-button-toggle-surface-aria-selected aria-selected:border-button-toggle-line-aria-selected " +
                    "disabled:text-button-toggle-foreground-disabled disabled:border-button-toggle-line-disabled " +
                    "group-disabled:text-button-toggle-foreground-disabled group-disabled:border-button-toggle-line-disabled " +
                    "transition-[color,background-color,border-color] duration-200 ease-[var(--ease-out-quad)]",
                // Accordion row-header. Full-width, left-aligned trigger that
                // sits flat on the page surface (no border/raise) — the
                // accordion-group components supply layout, padding, marker,
                // and indicator. Transparent at rest; sunken paper on hover
                // (square, no radius), mirroring the StepAccordion row hover.
                // The trigger's own `px-2` keeps the content off the edge — the
                // hover fill spans the full row. Title rides `foreground-base`
                // like the other text-bearing types.
                accordion:
                    "w-full justify-between text-left bg-transparent text-foreground-base " +
                    "hover:bg-surface-sunken disabled:text-button-transparent-foreground-disabled " +
                    "group-disabled:text-button-transparent-foreground-disabled"
            },
            interaction: {
                // `default` = no animation. Lets the type's color curve
                // apply with no extra behavior — for nav items, chips, and
                // any context where animation would be out of place.
                default: "",
                // lifted / light-bar / fillFromTop / fillFromBottom render
                // custom JSX branches below — class payload stays empty.
                lifted: "",
                "light-bar": "",
                underline:
                    "underline underline-offset-2 decoration-[1px] transition-[color,text-underline-offset] duration-200 ease-[var(--ease-out-quad)] hover:underline-offset-4 active:underline-offset-2 active:duration-100 aria-selected:underline-offset-2",
                fillFromTop: "",
                fillFromBottom: ""
            },
            size: {
                "2xs": cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT["2xs"] }),
                    "px-0 py-0 underline-offset-2"
                ),
                xs: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.xs }),
                    "px-3 py-1 underline-offset-2"
                ),
                fit: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.fit }),
                    "px-0 py-0 underline-offset-2"
                ),
                sm: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.sm }),
                    "px-3 py-1 underline-offset-2"
                ),
                md: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.md }),
                    "px-4 py-1 underline-offset-2"
                ),
                lg: cn(
                    textVariants({ variant: SIZE_TO_TEXT_VARIANT.lg }),
                    "px-6 py-3 underline-offset-2"
                )
            },
            outline: {
                true: "border border-solid",
                false: ""
            },
            fullWidth: {
                true: "w-full justify-center",
                false: ""
            }
        },
        compoundVariants: [
            {
                variant: "raised",
                outline: true,
                class: "border-button-raised-line-default"
            },
            {
                variant: "inverse-raised",
                outline: true,
                class: "border-button-inverse-raised-line-default"
            },
            {
                variant: "sunken",
                outline: true,
                class: "border-button-sunken-line-default"
            }
        ],
        defaultVariants: {
            variant: "raised",
            interaction: "lifted",
            size: "md",
            outline: false,
            fullWidth: false
        }
    }
);

/**
 * Helper for non-Button elements (link-as-button, trigger slots).
 *
 * NOTE: Currently used only inside the design-system showcase
 * (`DesignSystemPlayground.tsx`) — no production callers as of this
 * commit. The export is kept because the patterns it enables
 * (`<a className={buttonClasses(...)}>`, `<TooltipTrigger className={...}>`)
 * are legitimate API surfaces that may be needed later. Don't build
 * production constraints around it; the cva-driven typography +
 * `textVariants()` embedding is sufficient if a real caller appears.
 */
function buttonClasses(props: {
    variant?: ButtonVariant;
    interaction?: ButtonInteraction;
    size?: "xs" | "fit" | "sm" | "md" | "lg";
    outline?: boolean;
    fullWidth?: boolean;
}): string {
    return buttonVariants(props) ?? "";
}

interface ButtonProps
    extends Omit<ButtonPrimitive.Props, "size" | "disabled" | "render">,
        VariantProps<typeof buttonVariants> {
    leading?: React.ReactNode;
    trailing?: React.ReactNode;
    status?: ButtonStatus;
    /** Demo affordance — force the button's hover VISUAL on with no
     *  pointer present. CSS `:hover` / `group-hover:` can't be triggered
     *  from JS, so scripted sims (recorded "user sessions") use this to
     *  show the hover state. Emits the same hovered utilities the real
     *  `group-hover:` curve produces, unconditionally. Only meaningful in
     *  the idle status (gated on `hoverEnabled`); a no-op while pending /
     *  success / error. Default `false` — existing callers unaffected.
     *
     *  SUPERSEDED. New sims force state from outside the component instead:
     *  `sim/forceState.ts` + `styles/sim-force-state.css`, which work on the
     *  design-system Button and so do not pin a widget to this local copy.
     *  These props survive only for `widgets/yaas/programs/**` — the
     *  client-console Programs cluster, which cannot move to the DS Button
     *  yet for reasons unrelated to forced state (it needs `size="fit"` and
     *  the local `toggle-menu`, neither of which the DS ships). That is the
     *  rule for which files may still import this Button: if a file is not
     *  in that cluster, it should be on `@solomon-labs/design`. */
    forceHover?: boolean;
    /** Demo affordance — force the button's pressed / active VISUAL on
     *  with no pointer present, reading as a "click". Emits the same
     *  pressed utilities the real `group-active:` curve produces.
     *  Idle-only (gated on `hoverEnabled`); default `false`. */
    forcePressed?: boolean;
    holdToConfirm?: boolean;
    holdDurationMs?: number;
    onHoldConfirm?: () => void;
    holdingLabel?: React.ReactNode;
    completedLabel?: React.ReactNode;
    disabled?: boolean;
    "aria-selected"?: "true" | undefined;
    render?: React.ReactElement;
    children?: React.ReactNode;
    /** Override the default inner-content gap (between leading icon /
     *  children / trailing icon). Defaults to `gap-1` for xs/fit/sm
     *  (small text) and `gap-2` for md/lg (body text). Larger buttons
     *  with bigger text typically want more breathing room around an
     *  icon; smaller / inline buttons want tighter spacing. */
    gap?: ButtonGap;
}

type ButtonPointerEvent = Parameters<
    NonNullable<ButtonProps["onPointerDown"]>
>[0];
type ButtonKeyboardEvent = Parameters<NonNullable<ButtonProps["onKeyDown"]>>[0];
type ButtonMouseEvent = Parameters<NonNullable<ButtonProps["onClick"]>>[0];

/** Remove every `hover:` / `group-hover:` utility from a space-separated
 *  class string (including responsive/dark stacks like `md:hover:` and
 *  arbitrary-variant `[&:hover]:` forms). Used to neutralize the cva's
 *  hover color/offset curves when the button is in a non-default status,
 *  so the status visual is the only cursor-driven change. Active, focus,
 *  aria-selected, and disabled tokens are left intact. */
function stripHoverClasses(classes: string): string {
    return classes
        .split(/\s+/)
        .filter((token) => token.length > 0 && !/(^|:)hover:/.test(token))
        .join(" ");
}

/** Forced demo hover/press for variants whose hover/active visual is a cva
 *  COLOR curve (text/bg/border) rendered through the `default`/`underline`
 *  branch — the sim drives these without a real pointer, and CSS
 *  `:hover`/`:active` can't be JS-triggered.
 *
 *  These MUST be LITERAL class strings: Tailwind's scanner only generates the
 *  unprefixed utility if it appears verbatim in source. A runtime-derived
 *  class (e.g. slicing the `hover:` off the cva string) is never compiled, so
 *  it's a no-op — which is why the earlier attempt showed nothing. `cn`/
 *  twMerge then overrides the base color token with the forced one.
 *
 *  - `toggle` (Distribution mode buttons): hover = ember text + ember border
 *    (no fill); press/active = bedrock fill + active text + active border —
 *    matching the Figma toggle spec.
 *  - `transparent` (e.g. the underlined "+Add Tranche" button): hover = active
 *    = aria-selected = the single `foreground-hover` (ember) token, so both
 *    forced hover and forced press paint ember. No surface/border to change. */
function forcedColorClasses(
    variant: ButtonVariant | null,
    hover: boolean,
    pressed: boolean
): string {
    if (variant === "toggle") {
        if (pressed) {
            return "text-button-toggle-foreground-active bg-button-toggle-surface-active border-button-toggle-line-active";
        }
        if (hover) {
            return "text-button-toggle-foreground-hover border-button-toggle-line-hover";
        }
        return "";
    }
    if (variant === "transparent") {
        // hover === active === aria-selected → the same ember token.
        if (hover || pressed) {
            return "text-button-transparent-foreground-hover";
        }
        return "";
    }
    return "";
}

/** Pick the hover-fill color for `fillFromTop` / `fillFromBottom` based on
 *  variant. For types with a distinct hover/focus surface (tab, panel),
 *  match it; otherwise fall back to sunken paper. */
function fillSurfaceFor(variant: ButtonVariant): string {
    switch (variant) {
        case "tab":
            return "bg-button-tab-surface-focus";
        case "panel":
            return "bg-button-panel-surface-aria-selected";
        case "raised":
        case "inverse-raised":
        case "sunken":
        case "transparent":
        case "toggle":
        case "accordion":
            return "bg-surface-sunken";
    }
}

function Button({
    ref,
    variant = "raised",
    interaction = "lifted",
    size = "md",
    outline = false,
    fullWidth = false,
    leading,
    trailing,
    status,
    forceHover = false,
    forcePressed = false,
    holdToConfirm = false,
    holdDurationMs = 1400,
    onHoldConfirm,
    holdingLabel = "Keep holding...",
    completedLabel = "Confirmed",
    disabled,
    "aria-selected": ariaSelected,
    type = "button",
    className,
    children,
    gap,
    onPointerDown,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    onKeyDown,
    onKeyUp,
    onClick,
    ...props
}: ButtonProps): React.ReactElement {
    // `render` swaps the underlying element (link-as-button etc.). Base UI
    // must be told when that element is not a native <button>, otherwise it
    // warns at runtime and keeps button-only semantics on the wrong element.
    // An explicit `nativeButton` prop still wins.
    const nativeButton =
        props.nativeButton ??
        (props.render === undefined || props.render.type === "button");
    const primitiveProps = { ...props, nativeButton };

    // Inner-content gap: explicit `gap` prop wins, otherwise derived
    // from size (small text -> gap-1, body text -> gap-2). Applied on
    // the inner content span(s) of every render branch since that's
    // where children spacing actually materializes.
    const gapClass =
        gap !== undefined ? GAP_CLASS[gap] : SIZE_TO_GAP_CLASS[size ?? "md"];

    const [holdProgress, setHoldProgress] = React.useState(0);
    const [holdHeld, setHoldHeld] = React.useState(false);
    const [holdDone, setHoldDone] = React.useState(false);
    const holdStartRef = React.useRef<number | null>(null);
    const holdRafRef = React.useRef<number | null>(null);
    const holdConfirmedRef = React.useRef(false);

    React.useEffect(() => {
        return (): void => {
            if (holdRafRef.current !== null) {
                cancelAnimationFrame(holdRafRef.current);
            }
        };
    }, []);

    // `pending` propagates to the native disabled attribute so click +
    // keyboard activation both no-op until the caller updates status.
    const visuallyDisabled = disabled || holdDone || status === "pending";
    const effectiveInteraction = holdToConfirm ? "light-bar" : interaction;

    // Hover is only meaningful in the default, clickable state. Once the
    // button enters a non-default status (pending / success / error), the
    // status visual (light-bar flash, pending pulse, error burst, …) must
    // hold — a hover transform/fill would visually supersede it. Gate every
    // hover-driven visual on this single flag. `idle` and absent status are
    // the only hoverable states. (`visuallyDisabled` already suppresses
    // hover independently for disabled / hold-done / pending; this flag is
    // the broader status gate that also covers success / error.)
    const hoverEnabled = status === undefined || status === "idle";

    // Forced demo states. CSS pseudo-states can't be driven from JS, so the
    // scripted sims set these to make the hover / press visuals appear with
    // no pointer. They only make sense in the idle/hoverable state — gate on
    // `hoverEnabled` and on the button not being disabled so the forced
    // visual never fights a status (pending / success / error) or disabled
    // treatment. Each branch below emits the SAME utilities its real
    // `group-hover:` / `group-active:` curve produces, unconditionally.
    const forceHoverVisual =
        forceHover && hoverEnabled && !disabled && !holdToConfirm;
    const forcePressedVisual =
        forcePressed && hoverEnabled && !disabled && !holdToConfirm;

    function tickHold(now: number): void {
        if (holdStartRef.current === null) return;

        const elapsed = now - holdStartRef.current;
        const progress = Math.min(1, elapsed / holdDurationMs);
        setHoldProgress(progress);

        if (progress >= 1) {
            holdConfirmedRef.current = true;
            setHoldDone(true);
            setHoldHeld(false);
            holdStartRef.current = null;
            holdRafRef.current = null;
            onHoldConfirm?.();
            return;
        }

        holdRafRef.current = requestAnimationFrame(tickHold);
    }

    function startHold(): void {
        if (
            !holdToConfirm ||
            holdConfirmedRef.current ||
            visuallyDisabled ||
            holdStartRef.current !== null
        ) {
            return;
        }

        setHoldHeld(true);
        holdStartRef.current =
            performance.now() - holdProgress * holdDurationMs;
        holdRafRef.current = requestAnimationFrame(tickHold);
    }

    function cancelHold(): void {
        if (holdConfirmedRef.current) return;

        setHoldHeld(false);
        holdStartRef.current = null;
        if (holdRafRef.current !== null) {
            cancelAnimationFrame(holdRafRef.current);
            holdRafRef.current = null;
        }
        setHoldProgress(0);
    }

    React.useEffect(() => {
        if (!holdToConfirm || (!disabled && status !== "pending")) return;
        cancelHold();
    }, [disabled, holdToConfirm, status]);

    function handleHoldPointerDown(event: ButtonPointerEvent): void {
        onPointerDown?.(event);
        if (
            event.defaultPrevented ||
            (event.pointerType === "mouse" && event.button !== 0)
        ) {
            return;
        }

        event.currentTarget.setPointerCapture?.(event.pointerId);
        startHold();
    }

    function handleHoldPointerUp(event: ButtonPointerEvent): void {
        onPointerUp?.(event);
        cancelHold();
    }

    function handleHoldPointerLeave(event: ButtonPointerEvent): void {
        onPointerLeave?.(event);
        cancelHold();
    }

    function handleHoldPointerCancel(event: ButtonPointerEvent): void {
        onPointerCancel?.(event);
        cancelHold();
    }

    function handleHoldKeyDown(event: ButtonKeyboardEvent): void {
        onKeyDown?.(event);
        if (
            event.defaultPrevented ||
            (event.key !== " " && event.key !== "Enter")
        ) {
            return;
        }

        event.preventDefault();
        startHold();
    }

    function handleHoldKeyUp(event: ButtonKeyboardEvent): void {
        onKeyUp?.(event);
        if (event.key === " " || event.key === "Enter") {
            cancelHold();
        }
    }

    function handleHoldClick(event: ButtonMouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    const buttonEventProps = {
        onPointerDown: holdToConfirm ? handleHoldPointerDown : onPointerDown,
        onPointerUp: holdToConfirm ? handleHoldPointerUp : onPointerUp,
        onPointerLeave: holdToConfirm ? handleHoldPointerLeave : onPointerLeave,
        onPointerCancel: holdToConfirm
            ? handleHoldPointerCancel
            : onPointerCancel,
        onKeyDown: holdToConfirm ? handleHoldKeyDown : onKeyDown,
        onKeyUp: holdToConfirm ? handleHoldKeyUp : onKeyUp,
        onClick: holdToConfirm ? handleHoldClick : onClick
    };

    const holdDataState = holdToConfirm
        ? holdDone
            ? "done"
            : holdHeld
              ? "held"
              : "idle"
        : undefined;
    const holdLightBarProgress =
        holdToConfirm &&
        !holdDone &&
        (holdHeld ||
            holdProgress > 0 ||
            status === undefined ||
            status === "idle")
            ? holdProgress
            : undefined;
    const displayChildren = holdToConfirm
        ? holdHeld
            ? holdingLabel
            : holdDone
              ? completedLabel
              : children
        : children;

    const rawBaseClass = buttonVariants({
        variant,
        interaction: effectiveInteraction,
        size,
        outline,
        fullWidth
    });
    // When the button is in a non-default status, strip every `hover:` /
    // `group-hover:` utility the cva emitted (underline offset, toggle /
    // accordion / transparent fg/bg/line hover curves) so the status visual
    // is the only thing the cursor can change. Active / aria-selected /
    // focus / disabled variants are untouched — only hover tokens drop out.
    const baseClass = cn(
        hoverEnabled ? rawBaseClass : stripHoverClasses(rawBaseClass),
        // Forced demo states (sim): literal forced color classes so the
        // toggle's bg/text/border actually change without a pointer.
        // Mutually exclusive in practice (hover then press).
        forcedColorClasses(variant, forceHoverVisual, forcePressedVisual)
    );

    const content = (
        <>
            {leading && <span className="shrink-0">{leading}</span>}
            {displayChildren}
            {trailing && <span className="shrink-0">{trailing}</span>}
        </>
    );

    if (
        effectiveInteraction === "lifted" &&
        variant !== "transparent" &&
        variant !== "tab" &&
        variant !== "toggle" &&
        variant !== "accordion"
    ) {
        // 3-layer 3D button: thickness wrapper + right shadow + inner face.
        // At rest, the inner face fully covers the wrapper. On hover, the
        // face translates up (revealing the bottom thickness strip) and
        // the shadow strip translates right (revealing the side strip
        // beyond the right edge). On active, both translate back to 0 for
        // the press-down.
        //
        // Transparent and tab variants have no surface/depth, so the
        // lifted 3D treatment is meaningless — they fall through to the
        // default branch below (no side strip, no thickness, no fake
        // press animation against an invisible surface).
        //
        // Strip colors come from Figma node 1965:10124 — `button-lifted-side`
        // is shared across all variants; the bottom thickness diverges
        // between `bottom-base` (dark face) and `bottom-inverse` (light face).
        const liftedShadowBg = "bg-button-lifted-side";
        const thicknessBg =
            variant === "raised"
                ? "bg-button-lifted-bottom-base"
                : variant === "inverse-raised" ||
                    variant === "sunken" ||
                    variant === "panel"
                  ? "bg-button-lifted-bottom-inverse"
                  : ""; // transparent / tab — no surface, no thickness
        // Panel variant's bg diverges on aria-selected (outer Tab has the
        // attribute; inner span doesn't). group-aria-selected tracks the
        // outer's state so the inner face matches.
        const innerStateBg =
            variant === "panel"
                ? "group-aria-selected:bg-button-panel-surface-aria-selected"
                : "";

        return (
            <ButtonPrimitive
                ref={ref}
                type={nativeButton ? type : undefined}
                disabled={visuallyDisabled}
                aria-selected={ariaSelected}
                aria-busy={holdToConfirm && holdHeld ? true : undefined}
                data-slot="button"
                data-interaction="lifted"
                data-hold-state={holdDataState}
                className={cn(
                    "group relative inline-flex",
                    baseClass,
                    // Thickness override comes after baseClass so twMerge
                    // resolves the bg-color conflict to the dimmed value.
                    thicknessBg,
                    "p-0 border-0",
                    fullWidth ? "w-full" : "w-fit",
                    className
                )}
                {...buttonEventProps}
                {...primitiveProps}
            >
                <span
                    className={cn(
                        "pointer-events-none absolute right-0 top-0 bottom-0 w-[6px] transition-[translate] duration-300 ease-[var(--ease-out-quart)]",
                        liftedShadowBg,
                        // group-active stays always (press feedback is in
                        // scope-of-status-independent); only the hover lift
                        // is gated on the button being hoverable.
                        "group-active:!translate-x-0",
                        !visuallyDisabled &&
                            hoverEnabled &&
                            "group-hover:translate-x-[6px]",
                        // Forced demo press wins over forced demo hover —
                        // mirrors real pointer order (active overrides hover).
                        forcePressedVisual
                            ? "!translate-x-0"
                            : forceHoverVisual && "translate-x-[6px]",
                        // Aria-selected is already the "active" state — lift
                        // feedback on hover becomes noise. Pin to 0.
                        "group-aria-selected:!translate-x-0"
                    )}
                />
                <span
                    className={cn(
                        "relative inline-flex w-full items-center",
                        baseClass,
                        // `text-[inherit]` MUST come after baseClass so
                        // tailwind-merge resolves the text-color conflict
                        // in favor of inherit. Without this, baseClass's
                        // explicit `text-button-*-foreground-default`
                        // pins the inner face to the enabled color and
                        // the outer button's `disabled:text-...-disabled`
                        // never cascades in. (Disabled: modifiers don't
                        // apply to <span> directly — the cascade is the
                        // only path for the inner face to dim.)
                        "text-[inherit]",
                        innerStateBg,
                        gapClass,
                        "transition-[translate] duration-300 ease-[var(--ease-out-quart)]",
                        "group-active:!translate-y-0",
                        !visuallyDisabled &&
                            hoverEnabled &&
                            "group-hover:-translate-y-[6px]",
                        // Forced demo press wins over forced demo hover.
                        forcePressedVisual
                            ? "!translate-y-0"
                            : forceHoverVisual && "-translate-y-[6px]",
                        "group-aria-selected:!translate-y-0",
                        fullWidth && "justify-center"
                    )}
                >
                    {content}
                </span>
            </ButtonPrimitive>
        );
    }

    if (effectiveInteraction === "light-bar") {
        return (
            <ButtonPrimitive
                ref={ref}
                type={nativeButton ? type : undefined}
                disabled={visuallyDisabled}
                aria-selected={ariaSelected}
                aria-busy={holdToConfirm && holdHeld ? true : undefined}
                data-slot="button"
                data-interaction="light-bar"
                data-hold-state={holdDataState}
                className={cn(
                    "group relative",
                    holdToConfirm && "touch-none select-none",
                    baseClass,
                    className
                )}
                {...buttonEventProps}
                {...primitiveProps}
            >
                <span
                    className={cn(
                        "relative inline-flex w-full items-center justify-center",
                        // Forced demo press — a subtle scale-down reads as a
                        // click on the otherwise flat light-bar surface (which
                        // has no native press transform). Mirrors the lifted
                        // face's press-down with a transform-only change.
                        "transition-transform duration-150 ease-[var(--ease-out-quart)]",
                        forcePressedVisual && "scale-[0.98]",
                        gapClass
                    )}
                >
                    {content}
                </span>
                <ButtonLightBar
                    status={holdDone ? "success" : (status ?? "idle")}
                    progress={holdLightBarProgress}
                    forceHover={forceHoverVisual || forcePressedVisual}
                />
            </ButtonPrimitive>
        );
    }

    if (effectiveInteraction === "underline") {
        return (
            <ButtonPrimitive
                ref={ref}
                type={nativeButton ? type : undefined}
                disabled={visuallyDisabled}
                aria-selected={ariaSelected}
                aria-busy={holdToConfirm && holdHeld ? true : undefined}
                data-slot="button"
                data-interaction="underline"
                data-hold-state={holdDataState}
                className={cn(
                    baseClass,
                    // Forced demo hover — the real curve lifts the underline to
                    // `underline-offset-4` on hover (the color change comes
                    // through baseClass via forcedColorClasses). Forced press
                    // mirrors real `active:underline-offset-2` (= the rest
                    // offset), so only the forced-hover offset needs a literal.
                    forceHoverVisual &&
                        !forcePressedVisual &&
                        "underline-offset-4",
                    className
                )}
                {...buttonEventProps}
                {...primitiveProps}
            >
                <span className={cn("inline-flex items-center", gapClass)}>
                    {content}
                </span>
            </ButtonPrimitive>
        );
    }

    if (
        effectiveInteraction === "fillFromTop" ||
        effectiveInteraction === "fillFromBottom"
    ) {
        // Hover fill span anchored at top or bottom, grows to full height.
        // Color pulls from the variant's hover/focus surface token.
        const fillAnchor =
            effectiveInteraction === "fillFromTop" ? "top-0" : "bottom-0";
        // cva infers `variant` as `T | null | undefined`; the default
        // param above only catches `undefined`, so we coalesce `null` →
        // the same default ("raised") here before handing it to a fn
        // that requires a non-null `ButtonVariant`.
        const fillBg = fillSurfaceFor(variant ?? "raised");
        return (
            <ButtonPrimitive
                ref={ref}
                type={nativeButton ? type : undefined}
                disabled={visuallyDisabled}
                aria-selected={ariaSelected}
                aria-busy={holdToConfirm && holdHeld ? true : undefined}
                data-slot="button"
                data-interaction={effectiveInteraction}
                data-hold-state={holdDataState}
                className={cn(
                    "group relative overflow-hidden",
                    baseClass,
                    className
                )}
                {...buttonEventProps}
                {...primitiveProps}
            >
                <span
                    className={cn(
                        "pointer-events-none absolute inset-x-0 h-0 z-0 transition-[height] duration-300 ease-[var(--ease-out-cubic)]",
                        fillAnchor,
                        fillBg,
                        !visuallyDisabled &&
                            hoverEnabled &&
                            "group-hover:h-full",
                        // Forced demo hover/press — same full-height fill the
                        // real group-hover: produces, no pointer required.
                        (forceHoverVisual || forcePressedVisual) && "h-full"
                    )}
                />
                <span
                    className={cn(
                        "relative z-10 inline-flex items-center",
                        gapClass
                    )}
                >
                    {content}
                </span>
            </ButtonPrimitive>
        );
    }

    // `default` interaction — no animation. Just the type's color curve.
    return (
        <ButtonPrimitive
            ref={ref}
            type={nativeButton ? type : undefined}
            disabled={visuallyDisabled}
            aria-selected={ariaSelected}
            aria-busy={holdToConfirm && holdHeld ? true : undefined}
            data-slot="button"
            data-interaction="default"
            data-hold-state={holdDataState}
            className={cn(baseClass, className)}
            {...buttonEventProps}
            {...primitiveProps}
        >
            <span
                className={cn(
                    "inline-flex items-center",
                    gapClass,
                    // The accordion row-header is a full-width trigger: let the
                    // content span the button + spread (marker/title left,
                    // indicator right) instead of shrink-wrapping to content.
                    variant === "accordion" && "w-full justify-between"
                )}
            >
                {content}
            </span>
        </ButtonPrimitive>
    );
}

/* ── ButtonLightBar ─────────────────────────────────────────────
 * Animated underline bar for `interaction="light-bar"`.
 * Also consumed standalone by transaction success screens. */

interface ButtonLightBarProps {
    status?: ButtonStatus;
    progress?: number;
    /** Demo affordance — force the idle hover accent bars to their full
     *  hovered width with no pointer present (CSS `:hover` can't be driven
     *  from JS). Only honored in the idle status with no hold progress, the
     *  same conditions under which the real `group-hover:` bars would grow.
     *  Default `false`. */
    forceHover?: boolean;
}

const LIGHT_BAR_KEYFRAMES = `
@keyframes btn-underline-pending {
    0%   { transform: scaleX(0); animation-timing-function: cubic-bezier(0.0, 0.0, 0.2, 1); }
    20%  { transform: scaleX(1); animation-timing-function: cubic-bezier(0.8, 0.0, 1.0, 1); }
    100% { transform: scaleX(0); }
}
@keyframes btn-underline-success {
    0%   { transform: scaleX(0); }
    100% { transform: scaleX(1); }
}
@keyframes btn-underline-error-burst {
    0%    { opacity: 0; }
    12.5% { opacity: 1; }
    25%   { opacity: 0; }
    37.5% { opacity: 1; }
    50%   { opacity: 0; }
    62.5% { opacity: 1; }
    75%   { opacity: 0; }
    100%  { opacity: 1; }
}
@keyframes btn-underline-error-pulse {
    0%, 40% { opacity: 1; }
    70%     { opacity: 0.1; }
    100%    { opacity: 1; }
}
`;

function ButtonLightBar({
    status = "idle",
    progress,
    forceHover = false
}: ButtonLightBarProps): React.ReactElement {
    const boundedProgress =
        progress === undefined ? undefined : Math.max(0, Math.min(1, progress));

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: LIGHT_BAR_KEYFRAMES }} />
            <span className="absolute bottom-[-4px] left-0 h-[4px] w-full bg-line-base" />

            {boundedProgress !== undefined && (
                <>
                    <span
                        className="absolute bottom-[-4px] left-0 h-[4px] w-[calc(50%_+_1px)] origin-right bg-accent"
                        style={{
                            transform: `scaleX(${boundedProgress})`,
                            transition:
                                boundedProgress === 0
                                    ? "transform 200ms linear"
                                    : "none"
                        }}
                    />
                    <span
                        className="absolute bottom-[-4px] right-0 h-[4px] w-[calc(50%_+_1px)] origin-left bg-accent"
                        style={{
                            transform: `scaleX(${boundedProgress})`,
                            transition:
                                boundedProgress === 0
                                    ? "transform 200ms linear"
                                    : "none"
                        }}
                    />
                </>
            )}

            {status === "idle" && (
                <>
                    {boundedProgress === undefined && (
                        <>
                            <span
                                className={cn(
                                    "absolute bottom-[-4px] left-0 h-[4px] w-0 bg-accent transition-all duration-500 ease-[var(--ease-out-expo)] group-hover:w-[calc(50%_+_1px)]",
                                    // Forced demo hover — same hovered width
                                    // the real group-hover: produces, no
                                    // pointer required.
                                    forceHover && "w-[calc(50%_+_1px)]"
                                )}
                            />
                            <span
                                className={cn(
                                    "absolute bottom-[-4px] right-0 h-[4px] w-0 bg-accent transition-all duration-500 ease-[var(--ease-out-expo)] group-hover:w-[calc(50%_+_1px)]",
                                    forceHover && "w-[calc(50%_+_1px)]"
                                )}
                            />
                        </>
                    )}
                </>
            )}

            {status === "pending" && boundedProgress === undefined && (
                <>
                    <span
                        className="absolute bottom-[-4px] left-0 h-[4px] w-[calc(50%_+_1px)] origin-right"
                        style={{
                            backgroundColor: "rgb(var(--color-warning-base))",
                            animation:
                                "btn-underline-pending 2.5s linear infinite"
                        }}
                    />
                    <span
                        className="absolute bottom-[-4px] right-0 h-[4px] w-[calc(50%_+_1px)] origin-left"
                        style={{
                            backgroundColor: "rgb(var(--color-warning-base))",
                            animation:
                                "btn-underline-pending 2.5s linear infinite"
                        }}
                    />
                </>
            )}

            {status === "success" && boundedProgress === undefined && (
                // Success holds: the green bar is the state and must not be
                // superseded by a hover accent. No `group-hover:` bars here —
                // hover is disabled until status returns to idle.
                <>
                    <span
                        className="absolute bottom-[-4px] left-0 h-[4px] w-[calc(50%_+_1px)] origin-right"
                        style={{
                            backgroundColor: "rgb(var(--color-success-base))",
                            animation:
                                "btn-underline-success 400ms ease-out forwards"
                        }}
                    />
                    <span
                        className="absolute bottom-[-4px] right-0 h-[4px] w-[calc(50%_+_1px)] origin-left"
                        style={{
                            backgroundColor: "rgb(var(--color-success-base))",
                            animation:
                                "btn-underline-success 400ms ease-out forwards"
                        }}
                    />
                </>
            )}

            {status === "error" && boundedProgress === undefined && (
                // Error holds: the red burst + pulse IS the state (e.g. the
                // YaaS "Verification Failed" CTA). Hover is disabled in a
                // non-default status, so the bar no longer fades out and no
                // hover accent bars take over — the error visual is the only
                // thing on screen until status returns to idle.
                <span
                    className="absolute bottom-[-4px] left-0 h-[4px] w-full"
                    style={{
                        backgroundColor: "rgb(var(--color-error-base))",
                        animation:
                            "btn-underline-error-burst 600ms ease-out forwards, btn-underline-error-pulse 2.5s ease-in-out 600ms infinite"
                    }}
                />
            )}
        </>
    );
}

export { Button, ButtonLightBar, buttonVariants, buttonClasses };
export type { ButtonProps, ButtonVariant, ButtonInteraction };
